import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Edit2, Save, X, Trash2 } from 'lucide-react'

interface TestCase {
  input: string
  expected: 'pass' | 'fail'
  description: string
}

interface EditableTestCaseProps {
  testCase: TestCase
  onSave: (testCase: TestCase) => void
  onDelete: () => void
}

export default function EditableTestCase({ testCase, onSave, onDelete }: EditableTestCaseProps) {
  const [editing, setEditing] = useState(false)
  const [input, setInput] = useState(testCase.input)
  const [expected, setExpected] = useState(testCase.expected)
  const [description, setDescription] = useState(testCase.description)

  const handleSave = () => {
    onSave({ input, expected, description })
    setEditing(false)
  }

  const handleCancel = () => {
    setInput(testCase.input)
    setExpected(testCase.expected)
    setDescription(testCase.description)
    setEditing(false)
  }

  if (!editing) {
    return (
      <div className={`flex items-start gap-3 p-3 rounded-lg group ${
        testCase.expected === 'pass'
          ? 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900'
          : 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900'
      }`}>
        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          testCase.expected === 'pass'
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
        }`}>
          {testCase.expected === 'pass' ? '✓' : '✗'}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <code className={`text-sm font-mono px-2 py-0.5 rounded ${
              testCase.expected === 'pass'
                ? 'bg-green-100 dark:bg-green-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
            }`}>
              {testCase.input}
            </code>
            <span className={`text-xs font-bold uppercase ${
              testCase.expected === 'pass'
                ? 'text-green-700 dark:text-green-400'
                : 'text-red-700 dark:text-red-400'
            }`}>
              {testCase.expected}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{testCase.description}</p>
        </div>

        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
            className="h-8 w-8 p-0"
          >
            <Edit2 className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="border border-blue-300 dark:border-blue-700 rounded-lg p-4 bg-blue-50/50 dark:bg-blue-950/20 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Input Value
          </label>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g., 300x250"
            className="h-9"
          />
        </div>
        
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Expected Result
          </label>
          <select
            value={expected}
            onChange={(e) => setExpected(e.target.value as 'pass' | 'fail')}
            className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
          >
            <option value="pass">PASS</option>
            <option value="fail">FAIL</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">
          Description
        </label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., Standard medium rectangle"
          className="h-9"
        />
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" size="sm" onClick={handleCancel}>
          <X className="w-3 h-3 mr-1" />
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave}>
          <Save className="w-3 h-3 mr-1" />
          Save
        </Button>
      </div>
    </div>
  )
}

